import type { RepeatableInfo, RuntimeSourceInfo } from '../../../utils/repeatables/attachInstances';
import type { RowScope } from '../../../utils/repeatables/attachRowScopeIdentities';
import type { ComputeContextType, ContextMethodsState, ContextState } from './types';

// keyof-driven so a new RepeatableInfo field fails to compile until it is added
// to the cross-builder comparison below — same exhaustiveness guard STATE_KEYS /
// METHODS_KEYS use in ContextBuilder. Guards the field list the shared-base
// correctness depends on from silently drifting out of sync with the type.
const REPEATABLE_INFO_KEYS = Object.keys({
  data: true,
  primaryKey: true,
  itemPrimaryKeys: true,
  itemKeyToIndex: true,
} satisfies Record<keyof RepeatableInfo, true>) as (keyof RepeatableInfo)[];

// Pairs with the closure-scoped reuse in `createBuildRuntimeSourceInfo` so the
// steady-state 600-block hot path stays at O(1) via outer-record identity.
// On a fresh outer ref the comparator walks O(M) repeatables (`===` on
// per-parent `RepeatableInfo` refs that the factory preserves when parent
// state is unchanged) and O(N) child→parent string edges. M << N on real
// pages, and V8 interns short ASCII identifiers so the child walk is
// effectively pointer-equality.
export function isShallowEqualRuntimeSourceInfo(
  next: RuntimeSourceInfo | undefined,
  prev: RuntimeSourceInfo | undefined,
): boolean {
  if (next === prev) return true;
  if (!next || !prev) return false;

  const nextRepeatables = next.repeatables;
  const prevRepeatables = prev.repeatables;
  const nextRepeatableIds = Object.keys(nextRepeatables);
  if (nextRepeatableIds.length !== Object.keys(prevRepeatables).length) return false;
  for (const repeatableId of nextRepeatableIds) {
    if (nextRepeatables[repeatableId] !== prevRepeatables[repeatableId]) return false;
  }

  const nextChildToRepeatable = next.childToRepeatable;
  const prevChildToRepeatable = prev.childToRepeatable;
  const nextChildIds = Object.keys(nextChildToRepeatable);
  if (nextChildIds.length !== Object.keys(prevChildToRepeatable).length) return false;
  for (const childId of nextChildIds) {
    if (nextChildToRepeatable[childId] !== prevChildToRepeatable[childId]) return false;
  }
  return true;
}

/**
 * Cross-builder equality for RuntimeSourceInfo. Unlike isShallowEqualRuntimeSourceInfo
 * (which compares per-parent `RepeatableInfo` WRAPPER refs — only stable WITHIN one
 * builder's closure), this compares the wrapper's FIELDS. Each builder mints its own
 * RepeatableInfo wrappers, but their fields (`data`, `primaryKey`, `itemPrimaryKeys`,
 * `itemKeyToIndex`) are read straight from the shared block state, so they are
 * reference-identical across builders — which is what lets the process-wide readonly
 * base cache actually hit on repeatable pages.
 */
export function isRuntimeSourceInfoContentEqual(
  next: RuntimeSourceInfo | undefined,
  prev: RuntimeSourceInfo | undefined,
): boolean {
  if (next === prev) return true;
  if (!next || !prev) return false;

  const nextChild = next.childToRepeatable;
  const prevChild = prev.childToRepeatable;
  const nextChildIds = Object.keys(nextChild);
  if (nextChildIds.length !== Object.keys(prevChild).length) return false;
  for (const childId of nextChildIds) {
    if (nextChild[childId] !== prevChild[childId]) return false;
  }

  const nextRepeatables = next.repeatables;
  const prevRepeatables = prev.repeatables;
  const nextRepeatableIds = Object.keys(nextRepeatables);
  if (nextRepeatableIds.length !== Object.keys(prevRepeatables).length) return false;
  for (const repeatableId of nextRepeatableIds) {
    const nextInfo = nextRepeatables[repeatableId];
    const prevInfo = prevRepeatables[repeatableId];
    if (nextInfo === prevInfo) continue;
    if (!nextInfo || !prevInfo) return false;
    for (const field of REPEATABLE_INFO_KEYS) {
      if (nextInfo[field] !== prevInfo[field]) return false;
    }
  }
  return true;
}

/**
 * Cross-builder equality for `runtimeSourceBlockIds` (which children are themselves
 * runtime-source blocks). In production every builder on a page shares one ref from a
 * page-level useMemo, so `===` hits immediately; the size + membership walk only runs
 * when refs differ (distinct Sets with identical content). Both undefined → equal.
 */
export function isRuntimeSourceBlockIdsEqual(
  next: ReadonlySet<string> | undefined,
  prev: ReadonlySet<string> | undefined,
): boolean {
  if (next === prev) return true;
  if (!next || !prev) return false;
  if (next.size !== prev.size) return false;
  for (const id of next) {
    if (!prev.has(id)) return false;
  }
  return true;
}

/**
 * A captured readonly base context plus the exact inputs it was built from.
 * Backs the process-wide readonly base cache so one builder's base can be reused
 * by every other readonly builder on the same page. No `repeatableActions` field:
 * shared entries are readonly-only, where it is always undefined.
 */
export interface BaseContextSnapshot {
  inputs: ContextState;
  methods: ContextMethodsState;
  runtimeSourceInfo: RuntimeSourceInfo | undefined;
  runtimeSourceBlockIds: ReadonlySet<string> | undefined;
  base: ComputeContextType;
}

// Snapshot backing the shared per-ROW overlay cache. Sibling blocks in one Repeatable
// row compute an identical row-scoped context (same shared base, same per-row runtime
// state, same row scope), so the first sibling builds it and the rest adopt the result.
// Layers one level below BaseContextSnapshot: base is shared across ALL blocks; this
// shares the overlay across a row's siblings.
export interface RowOverlaySnapshot {
  base: ComputeContextType;
  blockStates: ContextState['blockStates'];
  rowScopeStack: readonly RowScope[] | undefined;
  runtimeSourceInfo: RuntimeSourceInfo | undefined;
  result: ComputeContextType;
}

// Process-wide cache of the readonly base context, keyed on the shared `blockStates`
// ref. Every readonly block on a page reads the SAME store slices, so the readonly base
// is identical across them — one builder computes it, the rest reuse it (turns a store
// notification's O(N-blocks) base rebuilds into O(1)). Safe to share ONLY in readonly,
// which bakes in no per-instance closures; mutable builds never touch this. Keyed on the
// blockStates ref, so distinct page-instances (distinct refs) never collide, and a store
// update (new ref) auto-invalidates + GCs the old entry. The stored base is treated as
// immutable, like every other cached base.
export const sharedReadonlyBaseCache = new WeakMap<object, BaseContextSnapshot>();

// Process-wide cache of the ROW overlay, keyed on the per-row `blocksRuntimeState` ref
// (shared by every sibling under one row's BlockRuntimeStateProvider). Turns a row's
// O(blocks-per-row) overlay rebuilds into O(1). Guarded like the base cache (readonly).
export const sharedRowOverlayCache = new WeakMap<object, RowOverlaySnapshot>();
